import { SepaGuard } from '@/components/guards/SepaGuard';
import { AutomationDrawer } from '@/features/domains/components/AutomationDrawer';
import { useSepaStatus } from '@/hooks/useBilling';
import { useSubscription } from '@/hooks/usePayment';
import { useUserAutomations } from '@/hooks/useUserAutomations';
import type {
  AutomationStatus,
  UserAutomation,
} from '@/types/automation.types';
import { AUTOMATION_TEMPLATES } from '@/types/automation.types';
import {
  Calendar,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Clock,
  GitGraph,
  Grid3x3,
  List,
  Mail,
  Search,
  Server,
  Settings,
  Users,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

// Search component with debounce - now receives props
const SearchBar = ({
  searchQuery,
  setSearchQuery,
}: {
  searchQuery: string;
  setSearchQuery: (query: string) => void;
}) => {
  const { t } = useTranslation();
  const [localSearch, setLocalSearch] = useState(searchQuery);

  // Sync with parent when searchQuery changes
  useEffect(() => {
    setLocalSearch(searchQuery);
  }, [searchQuery]);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchQuery(localSearch);
    }, 500);

    return () => clearTimeout(timer);
  }, [localSearch, setSearchQuery]);

  return (
    <div className="relative min-w-0 flex-1">
      <Search className="absolute top-1/2 left-3 h-5 w-5 -translate-y-1/2 text-neutral-400" />
      <input
        type="text"
        aria-label={t(
          'automations.search.label',
          'Rechercher une automatisation'
        )}
        placeholder={t(
          'automations.search.placeholder',
          'Search automations...'
        )}
        value={localSearch}
        onChange={e => setLocalSearch(e.target.value)}
        className="focus:border-primary-500 focus:ring-primary-100 w-full rounded-xl border border-neutral-300 py-3 pr-4 pl-10 transition-colors focus:ring-2"
      />
    </div>
  );
};

// Status filter component - now receives props
const StatusFilter = ({
  statusFilter,
  setStatusFilter,
}: {
  statusFilter: AutomationStatus;
  setStatusFilter: (status: AutomationStatus) => void;
}) => {
  const { t } = useTranslation();

  return (
    <div className="relative min-w-0 flex-shrink-0">
      <select
        aria-label={t('automations.filters.status.label', 'Filtrer par statut')}
        value={statusFilter}
        onChange={e => setStatusFilter(e.target.value as AutomationStatus)}
        className="focus:border-primary-500 focus:ring-primary-100 w-full appearance-none rounded-xl border border-neutral-300 bg-white px-4 py-3 pr-8 transition-colors focus:ring-2 sm:w-auto"
      >
        <option value="all">
          {t('automations.filters.status.all', 'All statuses')}
        </option>
        <option value="active">
          {t('automations.filters.status.active', 'Active')}
        </option>
        <option value="inactive">
          {t('automations.filters.status.inactive', 'Inactive')}
        </option>
      </select>
      <ChevronDown className="pointer-events-none absolute top-1/2 right-3 h-5 w-5 -translate-y-1/2 text-neutral-400" />
    </div>
  );
};

// Template filter component - now receives props
const TemplateFilter = ({
  templateFilter,
  setTemplateFilter,
}: {
  templateFilter: string;
  setTemplateFilter: (template: string) => void;
}) => {
  const { t } = useTranslation();

  return (
    <div className="relative min-w-0 flex-shrink-0">
      <select
        aria-label={t('automations.filters.template.label', 'Filtrer par type')}
        value={templateFilter}
        onChange={e => setTemplateFilter(e.target.value)}
        className="focus:border-primary-500 focus:ring-primary-100 w-full appearance-none rounded-xl border border-neutral-300 bg-white px-4 py-3 pr-8 transition-colors focus:ring-2 sm:w-auto"
      >
        <option value="all">
          {t('automations.filters.template.all', 'All types')}
        </option>
        <option value="generate-bulletin">
          {t(
            'automations.filters.template.generate-bulletin',
            'Generate Bulletins'
          )}
        </option>
        <option value="calculate-dsn">
          {t('automations.filters.template.calculate-dsn', 'Calculate DSN')}
        </option>
        <option value="generate-edition">
          {t(
            'automations.filters.template.generate-edition',
            'Generate Edition'
          )}
        </option>
      </select>
      <ChevronDown className="pointer-events-none absolute top-1/2 right-3 h-5 w-5 -translate-y-1/2 text-neutral-400" />
    </div>
  );
};

// View toggle component
const ViewToggle = ({
  viewMode,
  setViewMode,
}: {
  viewMode: 'grid' | 'list';
  setViewMode: (mode: 'grid' | 'list') => void;
}) => {
  const { t } = useTranslation();

  return (
    <div className="flex rounded-xl border border-neutral-300 bg-white">
      <button
        onClick={() => setViewMode('grid')}
        className={`flex items-center gap-2 rounded-l-xl px-4 py-3 text-sm font-medium transition-all ${viewMode === 'grid'
            ? 'bg-primary-600 text-white shadow-sm'
            : 'text-neutral-600 hover:bg-neutral-50'
          }`}
        title={t('automations.view.grid', 'Grid view')}
      >
        <Grid3x3 className="h-4 w-4" />
        <span className="hidden sm:block">
          {t('automations.view.grid', 'Grid')}
        </span>
      </button>
      <button
        onClick={() => setViewMode('list')}
        className={`flex items-center gap-2 rounded-r-xl px-4 py-3 text-sm font-medium transition-all ${viewMode === 'list'
            ? 'bg-primary-600 text-white shadow-sm'
            : 'text-neutral-600 hover:bg-neutral-50'
          }`}
        title={t('automations.view.list', 'List view')}
      >
        <List className="h-4 w-4" />
        <span className="hidden sm:block">
          {t('automations.view.list', 'List')}
        </span>
      </button>
    </div>
  );
};

// Pagination component - now receives props
const Pagination = ({
  currentPage,
  totalPages,
  totalItems,
  hasNext,
  hasPrev,
  goToPage,
  nextPage,
  prevPage,
}: {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  hasNext: boolean;
  hasPrev: boolean;
  goToPage: (page: number) => void;
  nextPage: () => void;
  prevPage: () => void;
}) => {
  const { t } = useTranslation();

  if (totalPages <= 1) return null;

  return (
    <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
      <p className="text-center text-sm text-neutral-600 sm:text-left">
        {t('automations.pagination.showing', 'Showing')}{' '}
        {(currentPage - 1) * 10 + 1}-{Math.min(currentPage * 10, totalItems)}{' '}
        {t('automations.pagination.of', 'of')} {totalItems}
      </p>

      <div className="flex items-center justify-center gap-2">
        <button
          onClick={prevPage}
          disabled={!hasPrev}
          className="rounded-lg border border-neutral-300 p-2 transition-colors hover:bg-neutral-50 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <ChevronLeft className="h-5 w-5" />
        </button>

        <div className="hidden items-center gap-2 sm:flex">
          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
            const page = currentPage <= 3 ? i + 1 : currentPage - 2 + i;
            if (page > totalPages) return null;

            return (
              <button
                key={page}
                onClick={() => goToPage(page)}
                className={`rounded-lg px-3 py-2 text-sm font-medium transition-colors ${page === currentPage
                    ? 'bg-primary-600 text-white'
                    : 'text-neutral-700 hover:bg-neutral-50'
                  }`}
              >
                {page}
              </button>
            );
          })}
        </div>

        {/* Mobile pagination info */}
        <div className="px-3 py-2 text-sm font-medium text-neutral-700 sm:hidden">
          {currentPage} / {totalPages}
        </div>

        <button
          onClick={nextPage}
          disabled={!hasNext}
          className="rounded-lg border border-neutral-300 p-2 transition-colors hover:bg-neutral-50 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <ChevronRight className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
};

// Compact Automation Card Component for Grid view
const AutomationCard = ({
  automation,
  onCardClick,
}: {
  automation: UserAutomation;
  onCardClick: (automation: UserAutomation) => void;
}) => {
  const { t } = useTranslation();

  const getStatusColor = (isActive: boolean) => {
    return isActive
      ? 'bg-success-100 text-success-700 border-success-200'
      : 'bg-neutral-100 text-neutral-600 border-neutral-200';
  };

  const getStatusText = (isActive: boolean) => {
    return isActive
      ? t('automations.status.active', 'Active')
      : t('automations.status.inactive', 'Inactive');
  };

  const getTemplateInfo = (templateId: string) => {
    const template =
      AUTOMATION_TEMPLATES[templateId as keyof typeof AUTOMATION_TEMPLATES];
    return template || { name: templateId, description: '' };
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const templateInfo = getTemplateInfo(automation.templateId);

  return (
    <div
      onClick={() => onCardClick(automation)}
      className="group hover:border-primary-200 w-full cursor-pointer rounded-xl border border-neutral-200 bg-white p-4 text-left transition-all duration-200 hover:shadow-md"
    >
      <div className="space-y-3">
        {/* Header avec icône et titre */}
        <div className="flex items-start gap-3">
          <div className="bg-primary-50 group-hover:bg-primary-100 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg transition-colors">
            <GitGraph className="text-primary-600 h-4 w-4" />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="group-hover:text-primary-700 truncate text-sm font-semibold text-neutral-900 transition-colors">
              {templateInfo.name}
            </h3>
            <p className="mt-0.5 line-clamp-1 text-xs text-neutral-500">
              {templateInfo.description}
            </p>
          </div>
        </div>

        {/* Status badge */}
        <div className="flex justify-start">
          <span
            className={`inline-flex items-center rounded-full border px-2 py-1 text-xs font-medium ${getStatusColor(automation.isActive)}`}
          >
            {getStatusText(automation.isActive)}
          </span>
        </div>

        {/* Informations principales */}
        <div className="space-y-2 text-xs">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5 text-neutral-500">
              <Server className="h-3.5 w-3.5" />
              <span className="font-medium">Domaine</span>
            </div>
            <span
              className="max-w-[120px] truncate font-medium text-neutral-900"
              title={automation.domain.name}
            >
              {automation.domain.name}
            </span>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5 text-neutral-500">
              <Settings className="h-3.5 w-3.5" />
              <span className="font-medium">Dossier</span>
            </div>
            <span
              className="max-w-[120px] truncate font-medium text-neutral-900"
              title={automation.folder.name}
            >
              {automation.folder.name}
            </span>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5 text-neutral-500">
              <Calendar className="h-3.5 w-3.5" />
              <span className="font-medium">Exécution</span>
            </div>
            <span className="font-medium text-neutral-900">
              Le {automation.dayOfMonth} à {String(automation.executionHour).padStart(2, '0')}h{String(automation.executionMinute).padStart(2, '0')}
            </span>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5 text-neutral-500">
              <Mail className="h-3.5 w-3.5" />
              <span className="font-medium">Email</span>
            </div>
            <span
              className="max-w-[140px] truncate font-medium text-neutral-900"
              title={automation.notificationEmail}
            >
              {automation.notificationEmail}
            </span>
          </div>

          <div className="flex items-center justify-between border-t border-neutral-100 pt-1">
            <div className="flex items-center gap-1.5 text-neutral-500">
              <Clock className="h-3.5 w-3.5" />
              <span className="font-medium">Créée</span>
            </div>
            <span className="font-medium text-neutral-900">
              {formatDate(automation.createdAt)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

// List Automation Card Component for List view
const ListAutomationCard = ({
  automation,
  onCardClick,
}: {
  automation: UserAutomation;
  onCardClick: (automation: UserAutomation) => void;
}) => {
  const { t } = useTranslation();

  const getStatusColor = (isActive: boolean) => {
    return isActive
      ? 'bg-success-100 text-success-700 border-success-200'
      : 'bg-neutral-100 text-neutral-600 border-neutral-200';
  };

  const getStatusText = (isActive: boolean) => {
    return isActive
      ? t('automations.status.active', 'Active')
      : t('automations.status.inactive', 'Inactive');
  };

  const getTemplateInfo = (templateId: string) => {
    const template =
      AUTOMATION_TEMPLATES[templateId as keyof typeof AUTOMATION_TEMPLATES];
    return template || { name: templateId, description: '' };
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const templateInfo = getTemplateInfo(automation.templateId);

  return (
    <div
      onClick={() => onCardClick(automation)}
      className="group hover:border-primary-200 w-full cursor-pointer rounded-xl border border-neutral-200 bg-white p-4 text-left transition-all duration-200 hover:shadow-md"
    >
      <div className="flex items-center gap-4">
        {/* Icon */}
        <div className="bg-primary-50 group-hover:bg-primary-100 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg transition-colors">
          <GitGraph className="text-primary-600 h-5 w-5" />
        </div>

        {/* Main content */}
        <div className="min-w-0 flex-1">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            {/* Left column - Template info */}
            <div className="min-w-0 flex-1">
              <h3 className="group-hover:text-primary-700 truncate text-base font-semibold text-neutral-900 transition-colors">
                {templateInfo.name}
              </h3>
              <p className="mt-0.5 line-clamp-1 text-sm text-neutral-500">
                {templateInfo.description}
              </p>
            </div>

            {/* Right column - Details grid */}
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-6">
              {/* Status */}
              <div className="flex items-center gap-2">
                <span
                  className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-medium ${getStatusColor(automation.isActive)}`}
                >
                  {getStatusText(automation.isActive)}
                </span>
              </div>

              {/* Domain and Folder */}
              <div className="flex flex-1 flex-col gap-1 text-sm sm:items-end">
                <div className="flex items-center gap-1.5 text-neutral-500">
                  <Server className="h-4 w-4" />
                  <span
                    className="max-w-[150px] truncate font-medium text-neutral-900"
                    title={automation.domain.name}
                  >
                    {automation.domain.name}
                  </span>
                </div>
                <div className="flex items-center gap-1.5 text-neutral-500">
                  <Settings className="h-4 w-4" />
                  <span
                    className="max-w-[200px] truncate font-medium text-neutral-900"
                    title={automation.folder.name}
                  >
                    {automation.folder.name}
                  </span>
                </div>
              </div>

              {/* Schedule and Email */}
              <div className="flex flex-1 flex-col gap-1 text-sm sm:items-end">
                <div className="flex items-center gap-1.5 text-neutral-500">
                  <Calendar className="h-4 w-4" />
                  <span className="font-medium text-neutral-900">
                    Le {automation.dayOfMonth} à {String(automation.executionHour).padStart(2, '0')}h{String(automation.executionMinute).padStart(2, '0')}
                  </span>
                </div>
                <div className="flex items-center gap-1.5 text-neutral-500">
                  <Mail className="h-4 w-4" />
                  <span
                    className="max-w-[230px] truncate font-medium text-neutral-900"
                    title={automation.notificationEmail}
                  >
                    {automation.notificationEmail}
                  </span>
                </div>
              </div>

              {/* Created date */}
              <div className="flex items-center gap-1.5 text-sm text-neutral-500 sm:min-w-[100px]">
                <Clock className="h-4 w-4" />
                <span className="font-medium text-neutral-900">
                  {formatDate(automation.createdAt)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Empty state component
const EmptyState = () => {
  const { t } = useTranslation();

  return (
    <div className="col-span-full rounded-xl border border-neutral-200 bg-neutral-50 p-8 text-center sm:p-12">
      <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-neutral-100 sm:h-16 sm:w-16">
        <GitGraph className="h-6 w-6 text-neutral-400 sm:h-8 sm:w-8" />
      </div>
      <h3 className="mb-2 text-lg font-semibold text-neutral-900">
        {t('automations.empty.title', 'No automations found')}
      </h3>
      <p className="mx-auto max-w-md text-sm text-neutral-600 sm:text-base">
        {t(
          'automations.empty.description',
          'Configure your first automations from the folders page'
        )}
      </p>
    </div>
  );
};

// Main Automations Page
export const AutomationsPage = () => {
  const { t } = useTranslation();

  // SEPA and Subscription check
  const { data: sepaData } = useSepaStatus();
  const hasValidSepa = sepaData?.hasValidMandate || sepaData?.isInGracePeriod || false;
  const { hasActiveSubscription, needsPayment } = useSubscription();

  // View mode state with localStorage persistence
  const [viewMode, setViewMode] = useState<'grid' | 'list'>(() => {
    const stored = localStorage.getItem('automations-view-mode');
    return (stored as 'grid' | 'list') || 'grid';
  });

  // Update localStorage when view mode changes
  useEffect(() => {
    localStorage.setItem('automations-view-mode', viewMode);
  }, [viewMode]);

  // Single hook call at the top level
  const {
    automations,
    isLoading,
    error,
    searchQuery,
    statusFilter,
    templateFilter,
    setSearchQuery,
    setStatusFilter,
    setTemplateFilter,
    currentPage,
    totalPages,
    totalItems,
    hasNext,
    hasPrev,
    goToPage,
    nextPage,
    prevPage,
  } = useUserAutomations();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedAutomation, setSelectedAutomation] =
    useState<UserAutomation | null>(null);

  const handleCardClick = (automation: UserAutomation) => {
    // Only open if has valid SEPA and active subscription
    // The SepaGuard will handle showing the modal if no SEPA
    if (!hasValidSepa || needsPayment) {
      return;
    }
    setSelectedAutomation(automation);
    setDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
    setSelectedAutomation(null);
  };

  // Convert automation to folder format for the drawer
  const selectedFolder = selectedAutomation
    ? {
      uuid: selectedAutomation.folder.uuid,
      domainUuid: selectedAutomation.domain.uuid,
      openPayeFolderUuid: selectedAutomation.folder.uuid, // Using folder uuid as fallback
      name: selectedAutomation.folder.name,
      path: selectedAutomation.folder.path,
      adresseEmail: '',
      telephone: '',
      nomContact: '',
      description: null,
      isActive: true,
      isArchived: false,
      archivedAt: null,
      isRestricted: false,
      isSynchro: true,
      metadata: {
        totalDocuments: 0,
        lastModified: new Date().toISOString(),
      },
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    }
    : null;

  if (isLoading) {
    return (
      <div className="space-y-6 sm:space-y-8">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
          <div className="bg-primary-100 flex h-12 w-12 items-center justify-center rounded-2xl">
            <GitGraph className="text-primary-600 h-6 w-6" />
          </div>
          <div className="min-w-0">
            <h1 className="text-2xl font-bold text-neutral-900 sm:text-3xl">
              {t('automations.title', 'Automations')}
            </h1>
            <p className="text-base text-neutral-600 sm:text-lg">
              {t('automations.subtitle', 'Manage your OpenPaye automations')}
            </p>
          </div>
        </div>

        <div className="animate-pulse space-y-6">
          <div className="flex flex-col gap-4 sm:flex-row">
            <div className="h-12 flex-1 rounded-xl bg-neutral-200"></div>
            <div className="h-12 w-full rounded-xl bg-neutral-200 sm:w-48"></div>
            <div className="h-12 w-full rounded-xl bg-neutral-200 sm:w-48"></div>
          </div>
          {/* Grid skeleton */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="h-64 rounded-xl bg-neutral-200"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6 sm:space-y-8">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
          <div className="bg-primary-100 flex h-12 w-12 items-center justify-center rounded-2xl">
            <GitGraph className="text-primary-600 h-6 w-6" />
          </div>
          <div className="min-w-0">
            <h1 className="text-2xl font-bold text-neutral-900 sm:text-3xl">
              {t('automations.title', 'Automations')}
            </h1>
            <p className="text-base text-neutral-600 sm:text-lg">
              {t('automations.subtitle', 'Manage your OpenPaye automations')}
            </p>
          </div>
        </div>

        <div className="border-red-200 bg-error-50 rounded-2xl border p-8 text-center sm:p-12">
          <div className="bg-error-100 mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full sm:h-16 sm:w-16">
            <Users className="text-error-600 h-6 w-6 sm:h-8 sm:w-8" />
          </div>
          <h3 className="text-error-900 mb-2 text-lg font-semibold">
            {t('automations.error.title', 'Error loading automations')}
          </h3>
          <p className="text-error-700 mx-auto max-w-md text-sm sm:text-base">
            {t('automations.error.description', 'Please try again later')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-6 sm:space-y-8">
        {/* Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-4">
            <div className="bg-primary-100 flex h-12 w-12 items-center justify-center rounded-2xl">
              <GitGraph className="text-primary-600 h-6 w-6" />
            </div>
            <div className="min-w-0">
              <h1 className="text-2xl font-bold text-neutral-900 sm:text-3xl">
                {t('automations.title', 'Automations')}
              </h1>
              <p className="text-base text-neutral-600 sm:text-lg">
                {t('automations.subtitle', 'Manage your OpenPaye automations')}
              </p>
            </div>
          </div>

          {/* View Toggle */}
          <ViewToggle viewMode={viewMode} setViewMode={setViewMode} />
        </div>

        {/* Search and Filters */}
        <div className="flex flex-col items-stretch gap-4 sm:flex-row sm:items-center">
          <SearchBar
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
          />
          <StatusFilter
            statusFilter={statusFilter}
            setStatusFilter={setStatusFilter}
          />
          <TemplateFilter
            templateFilter={templateFilter}
            setTemplateFilter={setTemplateFilter}
          />
        </div>

        {automations.length === 0 ? (
          <div className="grid grid-cols-1 gap-4">
            <EmptyState />
          </div>
        ) : (
          <>
            {/* Automations Display */}
            {viewMode === 'grid' ? (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5">
                {automations.map((automation: UserAutomation) => (
                  <SepaGuard key={automation.uuid}>
                    <AutomationCard
                      automation={automation}
                      onCardClick={handleCardClick}
                    />
                  </SepaGuard>
                ))}
              </div>
            ) : (
              <div className="space-y-3">
                {automations.map((automation: UserAutomation) => (
                  <SepaGuard key={automation.uuid}>
                    <ListAutomationCard
                      automation={automation}
                      onCardClick={handleCardClick}
                    />
                  </SepaGuard>
                ))}
              </div>
            )}

            {/* Pagination */}
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={totalItems}
              hasNext={hasNext}
              hasPrev={hasPrev}
              goToPage={goToPage}
              nextPage={nextPage}
              prevPage={prevPage}
            />
          </>
        )}
      </div>

      {/* Automation Drawer - only allow if has SEPA and subscription */}
      {hasValidSepa && hasActiveSubscription && (
        <AutomationDrawer
          isOpen={drawerOpen}
          onClose={handleDrawerClose}
          folder={selectedFolder}
        />
      )}
    </>
  );
};
